/**
 * Calibration team naming — the part that must not be got wrong.
 *
 * The raw calibration labels its two clusters 'Team A' and 'Team B' with no
 * idea which real team is which. Everything downstream — the names on every
 * analytic, the jersey colours, the home/away split — follows from the
 * mapping decided here, so it is pure, exported and tested rather than
 * inlined in the page.
 *
 * Ported from the Jinja page verbatim, including the order of its checks:
 * "no samples in this cluster" is reported before "no recorded direction",
 * and the two produce different hints.
 */

export type Variant = {
  labels?: number[]
  team_mapping?: Record<string, string>
  counts?: Record<string, number>
  params?: { outlier_percentile?: number; kmeans_cluster_centers?: unknown }
}
export type Bundle = { embedding?: number[][]; variants?: Record<string, Variant> }
export type Summary = {
  n_samples?: number
  feature_extractor?: string
  embedding?: number[][]
  extractors?: Record<string, Bundle>
  variants?: Record<string, Variant>
  sample_field_x?: (number | null)[]
  sample_half?: number[]
  sample_crops?: Record<string, string[]>
  sample_crop_indices?: number[]
}
export type TeamContext = {
  home: string | null
  away: string | null
  direction: 'left' | 'right' | null
}

export const CLUSTER_COLORS = ['#0969da', '#d1242f', '#8250df', '#1a7f37', '#9a6700', '#57606a']

export const VARIANT_LABELS: Record<string, string> = {
  kmeans: 'K-Means', kmeans_dbscan: 'K-Means + DBSCAN',
  gmm: 'GMM', gmm_dbscan: 'GMM + DBSCAN',
  kmeans3: 'K-Means (3)', kmeans3_dbscan: 'K-Means (3) + DBSCAN',
}
export const VARIANT_ORDER = ['kmeans', 'kmeans_dbscan', 'gmm', 'gmm_dbscan',
                              'kmeans3', 'kmeans3_dbscan']
export const EXTRACTOR_LABELS: Record<string, string> = {
  siglip: 'SigLIP', cv2: 'CV2 color', reid: 'ReID', siglip_reid: 'SigLIP + ReID',
}
/** Variants whose outlier cut can be retuned without a re-calibration. */
export const TUNABLE_VARIANTS = ['kmeans', 'kmeans_dbscan', 'kmeans3', 'kmeans3_dbscan']

/** Dual-extractor bundles nest {embedding, variants} per extractor; legacy
 *  bundles carry one set at the top level. */
export function activeBundle(summary: Summary | null, extractor: string | null): Bundle {
  if (summary?.extractors && extractor && summary.extractors[extractor]) {
    return summary.extractors[extractor]
  }
  return (summary ?? {}) as Bundle
}
export function activeVariants(summary: Summary | null, extractor: string | null) {
  return activeBundle(summary, extractor).variants ?? {}
}
export function activeEmbedding(summary: Summary | null, extractor: string | null) {
  return activeBundle(summary, extractor).embedding ?? summary?.embedding ?? []
}

export type Derived = {
  available: boolean
  invert?: boolean
  separation?: number
  noDirection?: boolean
}

/**
 * Which cluster is the home team, from where the clusters stood on the pitch.
 *
 * `sample_field_x` / `sample_half` give each embedding sample's field x in
 * metres and its half. In the first half the team defending the LEFT half has
 * mean x < 0, and the home team defends left when it ATTACKS right — which
 * pins the home name to a cluster.
 *
 * `invert === true` means the home team sits on the cluster the raw
 * calibration labeled 'Team B'.
 */
export function deriveForVariant(
  summary: Summary | null, extractor: string | null,
  variantName: string, ctx: TeamContext,
): Derived {
  const variant = summary ? activeVariants(summary, extractor)[variantName] : undefined
  const fx = summary?.sample_field_x
  const half = summary?.sample_half
  if (!variant || !Array.isArray(fx) || !Array.isArray(half)) return { available: false }

  const labels = variant.labels ?? []
  const mapping = variant.team_mapping ?? {}
  let sumA = 0, nA = 0, sumB = 0, nB = 0
  const n = Math.min(labels.length, fx.length, half.length)
  for (let i = 0; i < n; i++) {
    const x = fx[i]
    if (half[i] !== 1 || x == null) continue
    const team = mapping[String(labels[i])]
    if (team === 'Team A') { sumA += x; nA += 1 }
    else if (team === 'Team B') { sumB += x; nB += 1 }
  }
  if (!nA || !nB) return { available: false }

  // No recorded direction, no home/away naming: guessing here is the coin
  // flip RULED B forbids. The reviewer still sees Team A/B and keeps the
  // swap control.
  if (ctx.direction !== 'left' && ctx.direction !== 'right') {
    return { available: false, noDirection: true }
  }
  const meanA = sumA / nA
  const meanB = sumB / nB
  const homeDefendsLeft = ctx.direction !== 'left'   // attacks right => defends left
  const teamAIsLeft = meanA < meanB
  const homeIsTeamA = homeDefendsLeft ? teamAIsLeft : !teamAIsLeft
  return { available: true, invert: !homeIsTeamA, separation: Math.abs(meanA - meanB) }
}

/** The final cluster->name assignment: the derivation above (defaulting home
 *  on 'Team A' when underivable) XOR the reviewer's swap clicks. */
export function finalHomeOnTeamA(derived: Derived, userSwapped: boolean): boolean {
  const derivedInvert = !!(derived.available && derived.invert)
  return derivedInvert === userSwapped
}

/** 'Team A' -> e.g. 'SWE (Team A)' under the current assignment. */
export function displayNameFor(
  rawTeam: string, ctx: TeamContext | null, homeOnTeamA: boolean,
): string {
  if (rawTeam !== 'Team A' && rawTeam !== 'Team B') return rawTeam
  if (!ctx || !ctx.home || !ctx.away) return rawTeam
  const isHome = (rawTeam === 'Team A') === homeOnTeamA
  return `${isHome ? ctx.home : ctx.away} (${rawTeam})`
}

/** The sentence under the scatter: what the naming was derived from, and how
 *  much to trust it. */
export function teamHint(derived: Derived, ctx: TeamContext, userSwapped: boolean): string {
  const swapNote = userSwapped ? ' · Teams swapped by reviewer.' : ''
  if (!derived.available) {
    return (derived.noDirection
      ? 'Attack direction not set — set it under Teams & direction below. Defaulting home = Team A. '
      : 'No field positions in this calibration bundle — defaulting home = Team A. ')
      + 'Direction-based naming uncertain — check crops.' + swapNote
  }
  let text = 'Team naming derived from H1 field positions (home attacks '
    + `${ctx.direction}). Cluster separation: ${(derived.separation ?? 0).toFixed(1)} m.`
  if ((derived.separation ?? 0) < 5) text += ' Direction-based naming uncertain — check crops.'
  return text + swapNote
}

/** True when the two team counts differ by more than 30% — the imbalance
 *  that usually means the clustering split one team in half. */
export function countsImbalanced(counts: Record<string, number>): boolean {
  const teamCounts = Object.entries(counts)
    .filter(([team]) => team.toLowerCase() !== 'other')
    .map(([, c]) => c)
  if (teamCounts.length < 2) return false
  const hi = Math.max(...teamCounts), lo = Math.min(...teamCounts)
  return lo > 0 && (hi - lo) / lo > 0.3
}

export type ApprovePayload = {
  variant: string
  swap_teams: boolean
  team_names: Record<string, string>
  feature_extractor?: string
  outlier_percentile?: number
  team_colors?: Record<string, string>
  home_team_start_direction?: 'left' | 'right'
}

/**
 * The approve call's body.
 *
 * SWAP SEMANTICS, and the reason this is a tested function: the worker's
 * `swap_teams` flag relabels the raw calibration's internal 'Team A' <->
 * 'Team B'. Our final mapping is the field-x derivation XOR the reviewer's
 * swap clicks. We send swap_teams = true iff that mapping puts the HOME team
 * on the cluster the raw calibration labeled 'Team B' — so after the worker
 * applies the flag, the OUTPUT's 'Team A' is ALWAYS the home team.
 * `team_names` and `team_colors` are keyed to that post-swap meaning, which
 * is what every downstream reader of job.calibration assumes.
 */
export function buildApprovePayload(args: {
  variant: string
  summary: Summary | null
  extractor: string | null
  tunedPercentile: number | undefined
  homeOnTeamA: boolean
  teamColors: Record<string, string>
  ctx: TeamContext
}): ApprovePayload {
  const { variant, summary, extractor, tunedPercentile, homeOnTeamA, teamColors, ctx } = args
  const body: ApprovePayload = {
    variant,
    swap_teams: !homeOnTeamA,
    team_names: { 'Team A': ctx.home || 'Team A', 'Team B': ctx.away || 'Team B' },
  }
  // Dual-extractor bundles: tell the worker which embedding space was approved.
  if (summary?.extractors && extractor) body.feature_extractor = extractor
  if (tunedPercentile != null) body.outlier_percentile = tunedPercentile
  // Home's H1 direction, meaning the home team named in ctx. swap_teams
  // does not change it: swap_teams only moves home onto output Team A.
  if (ctx.direction === 'left' || ctx.direction === 'right') {
    body.home_team_start_direction = ctx.direction
  }

  const rawHome = homeOnTeamA ? 'Team A' : 'Team B'
  const rawAway = homeOnTeamA ? 'Team B' : 'Team A'
  if (teamColors[rawHome] || teamColors[rawAway]) {
    body.team_colors = {}
    if (teamColors[rawHome]) body.team_colors['Team A'] = teamColors[rawHome]
    if (teamColors[rawAway]) body.team_colors['Team B'] = teamColors[rawAway]
  }
  return body
}

/** "mm:ss", "h:mm:ss" or raw seconds -> seconds. null for blank, NaN for
 *  anything that is not a time — the caller refuses to save on NaN. */
/** Seconds back to the mm:ss the form takes — the inverse of parseClock,
 *  so a stored value round-trips through the input unchanged. */
export function formatClock(s: number | null | undefined): string {
  if (s == null || Number.isNaN(s)) return ''
  const m = Math.floor(s / 60)
  const sec = Math.round(s % 60)
  return `${m}:${String(sec).padStart(2, '0')}`
}

export function parseClock(v: string | undefined): number | null {
  const t = (v ?? '').trim()
  if (!t) return null
  const parts = t.split(':').map(Number)
  if (parts.some((n) => Number.isNaN(n))) return NaN
  const [a = 0, b = 0, c = 0] = parts
  return parts.length === 3 ? a * 3600 + b * 60 + c
    : parts.length === 2 ? a * 60 + b : a
}

/**
 * A copy of the summary with one variant's labels and counts replaced.
 *
 * The refit endpoint returns new labels for the ACTIVE embedding, and the
 * active embedding lives in one of two places: under `extractors[fx]` for a
 * dual-extractor bundle, at the top level for a legacy one. Writing to the
 * wrong branch does not throw — it silently leaves the screen showing the
 * old clustering — so the branch is chosen here, once.
 */
export function withRefitVariant(
  summary: Summary, extractor: string | null, variantName: string,
  labels: number[], counts: Record<string, number>,
): Summary {
  const patch = (vs: Record<string, Variant>): Record<string, Variant> => ({
    ...vs,
    [variantName]: { ...(vs[variantName] ?? {}), labels, counts },
  })
  if (summary.extractors && extractor && summary.extractors[extractor]) {
    const bundle = summary.extractors[extractor]
    return {
      ...summary,
      extractors: {
        ...summary.extractors,
        [extractor]: { ...bundle, variants: patch(bundle.variants ?? {}) },
      },
    }
  }
  return { ...summary, variants: patch(summary.variants ?? {}) }
}

/** A jersey colour and the crops it was read from (or set beside). */
export type BucketColor = { urls: string[]; hex: string }

/** The colour of the entry whose crops overlap `urls` most, when that
 *  overlap is more than half of the bucket — else null.
 *
 *  Colours belong to PLAYERS, not to the label 'Team A' / 'Team B': the
 *  feature extractors label the same clusters differently (IPS@MANU on dev,
 *  2026-10-10: siglip's 'Team A' is cv2's 'Team B'), and a re-fit moves
 *  crops between labels. Keying colours by label carried a colour across to
 *  the other team when the reviewer switched extractor, and approve saved
 *  the two teams' jerseys swapped. */
export function colorForBucket(urls: string[], entries: BucketColor[]): string | null {
  if (!urls.length) return null
  const mine = new Set(urls)
  let best: string | null = null
  let bestShare = 0.5
  for (const e of entries) {
    const shared = e.urls.filter((u) => mine.has(u)).length
    const share = shared / mine.size
    if (share > bestShare) {
      bestShare = share
      best = e.hex
    }
  }
  return best
}

function rgb(hex: string): [number, number, number] | null {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex)
  return m ? [parseInt(m[1]!, 16), parseInt(m[2]!, 16), parseInt(m[3]!, 16)] : null
}

function distance(a: string, b: string): number | null {
  const x = rgb(a)
  const y = rgb(b)
  if (!x || !y) return null
  return Math.hypot(x[0] - y[0], x[1] - y[1], x[2] - y[2])
}

/** True when the jersey colour declared at upload for the home team is
 *  nearer the colour of the cluster named AWAY than of the one named home
 *  — the naming, or the colours, are likely swapped. False when either
 *  colour is missing. */
export function uploadColorDisagrees(
  uploadHome: string | null | undefined,
  homeHex: string | null | undefined, awayHex: string | null | undefined,
): boolean {
  if (!uploadHome || !homeHex || !awayHex) return false
  const toHome = distance(uploadHome, homeHex)
  const toAway = distance(uploadHome, awayHex)
  return toHome != null && toAway != null && toAway < toHome
}
